<template>
    <fieldset class="grid grid-cols-2">
        <div>
            <DsButton
                size="md"
                variant="primary"
                @click="reportWarning"
            >
                Fire warning
            </DsButton>
        </div>
        <div>
            <DsButton
                size="md"
                variant="primary"
                @click="reportError"
                >Fire error</DsButton
            >
        </div>
    </fieldset>
    <fieldset class="mb-4 grid gap-4 border px-4 py-2">
        <legend>Controls</legend>
        <div>
            <label class="inline-flex items-center gap-2">
                <input
                    v-model="useDarkBg"
                    type="checkbox"
                />
                Show dark background
            </label>
        </div>
        <div>
            Leading icons
            <div class="flex items-center gap-6 pt-1">
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="leadingIcon"
                        type="radio"
                        value="fa-chevron-left"
                    />
                    <font-awesome-icon icon="fas fa-chevron-left" />
                </label>
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="leadingIcon"
                        type="radio"
                        value="fa-times"
                    />
                    <font-awesome-icon icon="fas fa-times" />
                </label>
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="leadingIcon"
                        type="radio"
                        value="fa-check"
                    />
                    <font-awesome-icon icon="fas fa-check" />
                </label>
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="leadingIcon"
                        type="radio"
                        :value="leadingIconCustom"
                    />
                    <font-awesome-icon :icon="`fas ${leadingIconCustom}`" />
                    <input
                        v-model="leadingIconCustom"
                        class="rounded-md p-1"
                        type="text"
                    />
                </label>
            </div>
        </div>
        <div>
            Trailing icons
            <div class="flex items-center gap-6 pt-1">
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="trailingIcon"
                        type="radio"
                        value="fa-chevron-right"
                    />
                    <font-awesome-icon icon="fas fa-chevron-right" />
                </label>
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="trailingIcon"
                        type="radio"
                        value="fa-times"
                    />
                    <font-awesome-icon icon="fas fa-times" />
                </label>
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="trailingIcon"
                        type="radio"
                        value="fa-check"
                    />
                    <font-awesome-icon icon="fas fa-check" />
                </label>
                <label class="inline-flex items-center gap-2">
                    <input
                        v-model="trailingIcon"
                        type="radio"
                        :value="trailingIconCustom"
                    />
                    <font-awesome-icon :icon="`fas ${trailingIconCustom}`" />
                    <input
                        v-model="trailingIconCustom"
                        class="rounded-md p-1"
                        type="text"
                    />
                </label>
            </div>
        </div>
    </fieldset>

    <h2>Link button</h2>
    <DsButtonSampleSet
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="link-button"
    />

    <h2>Primary button</h2>
    <DsButtonSampleSet
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="primary"
    />

    <h2>Secondary button</h2>
    <DsButtonSampleSet
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="secondary"
    />

    <h2>Tertiary button</h2>
    <DsButtonSampleSet
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="tertiary"
    />

    <h2>Primary destructive button</h2>
    <DsButtonSampleSet
        v-if="!useDarkBg"
        destructive
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="primary"
    />
    <em v-else>No variant for dark background</em>

    <h2>Secondary destructive button</h2>
    <DsButtonSampleSet
        v-if="!useDarkBg"
        destructive
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="secondary"
    />
    <em v-else>No variant for dark background</em>

    <h2>Tertiary destructive button</h2>
    <DsButtonSampleSet
        v-if="!useDarkBg"
        destructive
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="tertiary"
    />
    <em v-else>No variant for dark background</em>

    <h2>Primary link</h2>
    <DsButtonSampleSet
        href="https://www.google.com/"
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="primary"
    />

    <h2>Secondary link</h2>
    <DsButtonSampleSet
        href="https://www.google.com/"
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="secondary"
    />

    <h2>Tertiary link</h2>
    <DsButtonSampleSet
        href="https://www.google.com/"
        :leading="leadingProp"
        :trailing="trailingProp"
        :use-dark-bg
        variant="tertiary"
    />
</template>

<script lang="ts" setup>
import { notifyError, notifyWarning } from '@/bugsnag';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import { computed, ref } from 'vue';
import DsButtonSampleSet from './DsButtonSampleSet.vue';

const leadingIcon = ref('fa-chevron-left');
const trailingIcon = ref('fa-chevron-right');

const leadingIconCustom = ref('fa-circle-question');
const trailingIconCustom = ref('fa-circle-question');

const leadingProp = computed(() => `fas ${leadingIcon.value}`);
const trailingProp = computed(() => `fas ${trailingIcon.value}`);

const useDarkBg = ref(false);

function doubleNestedCall() {
    nestedCall();
}
function nestedCall() {
    throw new Error('Something broke real bad');
}

function reportWarning() {
    notifyWarning('Something weird is afoot', 'DsButtonSamples context', {
        component: 'DsButtonSamples',
        nestedData: { foo: 'bar' },
    });
}

function reportError() {
    try {
        doubleNestedCall();
    } catch (error) {
        notifyError(error, 'DsButtonSamples context', {
            component: 'DsButtonSamples',
            nestedData: { foo: 'bar', doubleNested: { somethingElse: false } },
        });
    }
}
</script>

<style scoped lang="scss">
h2 {
    margin-top: 2rem;
    margin-bottom: 1rem;

    &:first-child {
        margin-top: 0;
    }
}

h3 {
    font-weight: 500;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
}

.dark-bg {
    @apply bg-ds-gray-900 text-white;
}
</style>
